import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { myCartFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import type {
  CartBuyerQuery,
  CartBuyerQueryVariables,
} from './__generated__/usePollCartBought.graphql.ts';

const CART_BUYER_QUERY = gql`
  query CartBuyerQuery(
    $id: String!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    tokens {
      primaryOffer(id: $id) {
        id
        buyer {
          slug
        }
        anyCards {
          slug
          id
        }
      }
    }
    currentUser {
      slug
      ...CurrentUserProvider_myCart
    }
  }
  ${myCartFragment}
` as TypedDocumentNode<CartBuyerQuery, CartBuyerQueryVariables>;

export const usePollCartBought = (
  enabled: boolean,
  sport: Sport,
  propsId: string | undefined,
  cb: (successOfferId: string | false, anyCardIds?: string[]) => void
) => {
  const [id, setId] = useState<string | undefined>(propsId);
  const { currentUser } = useCurrentUserContext();

  const { data } = useQuery(CART_BUYER_QUERY, {
    variables: {
      id: idFromObject(id!),
      sport,
    },
    pollInterval: enabled ? 1000 : 0,
    skip: !enabled || !id,
  });

  const { primaryOffer } = data?.tokens || {};
  const anyCardIds = primaryOffer?.anyCards
    .map(card => card.id)
    .filter(Boolean);

  useEffect(() => {
    const offerId =
      !!currentUser &&
      !!primaryOffer?.buyer &&
      primaryOffer?.buyer.slug === currentUser.slug &&
      primaryOffer.id;
    if (enabled) {
      if (offerId) {
        cb(offerId, anyCardIds);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setId(undefined);
      }
    }
  }, [
    cb,
    enabled,
    currentUser,
    primaryOffer?.buyer,
    primaryOffer?.id,
    id,
    anyCardIds,
  ]);

  if (!id && propsId) {
    setId(propsId);
  }
  if (propsId && id && id !== propsId) {
    setId(propsId);
  }
};
